import React from 'react';
import { Card, CardContent, Box, Typography, Chip, Button, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

const IntegrationCard = ({
  integration,
  isConnected,
  onManage,
  onConnect,
  onSync,
  isLoading,
  syncingIntegrations = new Set(),
  connectedItems = []
}) => {
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: 2,
        borderColor: isConnected ? 'success.main' : 'transparent',
        borderLeftWidth: isConnected ? 4 : 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
        '@keyframes spinContinuous': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              fontSize: 28,
              width: 50,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.subtle',
              borderRadius: '10px',
              flexShrink: 0,
            }}
          >
            {integration.icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
            >
              {integration.name}
            </Typography>
            {isConnected && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'success.main',
                        borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'pulse 2s ease-in-out infinite',
                      }}
                    />
                    {connectedItems.length} {connectedItems.length === 1 ? integration.singularLabel || 'connection' : integration.pluralLabel || 'connections'}
                  </Box>
                }
                sx={{ height: 'auto', '& .MuiChip-label': { py: 0.25 } }}
              />
            )}
          </Box>
        </Box>

        {/* Description */}
        <Typography
          sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.5, mb: 2 }}
        >
          {integration.description}
        </Typography>

        {/* Connected items list */}
        {isConnected && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              bgcolor: 'background.subtle',
              borderRadius: 2,
              fontFamily: '"Courier New", monospace',
              fontSize: '0.85rem',
            }}
          >
            {connectedItems.slice(0, 3).map((item, index) => {
              const isSyncing = syncingIntegrations.has(item.id);
              return (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, color: 'text.secondary' }}
                >
                  <Typography component="span" sx={{ color: 'divider', fontWeight: 'bold', fontFamily: 'inherit' }}>
                    └─
                  </Typography>
                  <Typography component="span" sx={{ flex: 1, color: 'text.primary', fontFamily: 'inherit' }}>
                    {item.name}
                  </Typography>
                  {item.count && (
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        bgcolor: 'background.paper',
                        px: 1,
                        borderRadius: 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      {item.count}
                    </Typography>
                  )}
                  {integration.supportSync && (
                    <IconButton
                      size="small"
                      onClick={() => onSync(item.id, item.name)}
                      disabled={isLoading || isSyncing}
                      title={isSyncing ? 'Syncing in progress...' : 'Sync this organization'}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: isSyncing ? 'info.main' : 'success.main',
                        color: 'success.contrastText',
                        '&:hover': {
                          bgcolor: isSyncing ? 'info.dark' : 'success.dark',
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'action.disabledBackground',
                          color: 'action.disabled',
                        },
                      }}
                    >
                      <RefreshIcon
                        sx={{
                          fontSize: 14,
                          ...(isSyncing && {
                            animation: 'spinContinuous 1s linear infinite',
                          }),
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
              );
            })}
            {connectedItems.length > 3 && (
              <Typography
                sx={{
                  py: 0.5,
                  color: 'info.main',
                  fontStyle: 'italic',
                  fontFamily: 'system-ui',
                }}
              >
                +{connectedItems.length - 3} more (view all to sync)
              </Typography>
            )}
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
          {isConnected ? (
            <>
              <Button
                variant="contained"
                onClick={onManage}
                disabled={isLoading}
                sx={{ flex: 1 }}
              >
                Manage
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onConnect}
                disabled={isLoading}
                sx={{ whiteSpace: 'nowrap' }}
              >
                + Add More
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={onConnect}
              disabled={integration.status !== 'Available'}
            >
              {integration.status === 'Available' ? 'Connect' : 'Coming Soon'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default IntegrationCard;
